import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Global page-loader overlay. Mirrors the original `<page-loader>` directive
 * + `$pageloader` service. Show/hide is driven from anywhere via `usePageLoader()`.
 */
interface PageLoaderState {
  visible: boolean;
  show: () => void;
  hide: () => void;
}

const PageLoaderContext = createContext<PageLoaderState | undefined>(undefined);

export function PageLoaderProvider({ children }: { children: ReactNode }) {
  const [visible, setVisible] = useState(false);
  const value = useMemo<PageLoaderState>(
    () => ({ visible, show: () => setVisible(true), hide: () => setVisible(false) }),
    [visible],
  );
  return <PageLoaderContext.Provider value={value}>{children}</PageLoaderContext.Provider>;
}

export function usePageLoader(): PageLoaderState {
  // Allow components to call usePageLoader() even when the provider isn't mounted —
  // the no-op variant keeps the call site simple.
  const ctx = useContext(PageLoaderContext);
  return ctx ?? { visible: false, show: () => {}, hide: () => {} };
}

export default function PageLoader() {
  const { visible } = usePageLoader();
  if (!visible) return null;
  return (
    <div className="page-loader">
      <div className="pg-loader"></div>
    </div>
  );
}
